import {assert} from 'Asserts';
import {JSONValue} from 'util/Types';

import {AnyType} from './AnyType';
import {ComplexType, List, Nullable, Union} from './ComplexTypes';

// TODO(BNCH-30869): Drop old Insights DataType enum in favor of this (client/ui-library/charting/Types.ts)
export type BenchlingTypeDef = LeafType | ComplexType | AnyType;

// Keep in sync with benchling/data_types/benchling_type_def/leaf_types.py
// and benchling/pipelines/step_type_library/manifests/types/config_specs.yaml
export enum LeafType {
  BOOLEAN = 'Boolean',
  DATETIME = 'Datetime',
  DATE = 'Date',
  DECIMAL = 'Decimal',
  INTEGER = 'Integer',
  JSON = 'Json',
  TEXT = 'Text',

  BENCHLING_OBJECT = 'BenchlingObject',
  BLOB = 'Blob',
  CHART = 'Chart',
  FILE = 'File',
  SCRATCH_FILE = 'ScratchFile',
  DATA_FRAME = 'DataFrame',
  DATASET = 'Dataset',
  DROPDOWN_OPTION = 'DropdownOption',
  BATCH = 'Batch',
  BIOENTITY = 'Bioentity',
  BASIC_FOLDER_ITEM = 'BasicFolderItem',
  MIXTURE = 'Mixture',
  MOLECULE = 'Molecule',
  PROTEIN = 'Protein',
  SEQUENCE = 'Sequence',
  ENTRY = 'Entry',
  PROTOCOL = 'Protocol',
  REQUEST = 'Request',
  RESULT = 'Result',
  RUN = 'Run',
  STORABLE = 'Storable',
  BOX = 'Box',
  CONTAINER = 'Container',
  LOCATION = 'Location',
  PLATE = 'Plate',
  PROCESS = 'Process',
  PROCESS_TASK = 'ProcessTask',
  PROCESS_OUTPUT = 'ProcessOutput',
  STUDY = 'Study',
  TEAM = 'Team',
  TEST_ORDER = 'TestOrder',
  USER = 'User',
  WORKSHEET = 'Worksheet',
}

export const LeafTypeToFriendlyName: {[key in LeafType]: string} = {
  [LeafType.BOOLEAN]: 'Boolean',
  [LeafType.DATETIME]: 'Datetime',
  [LeafType.DATE]: 'Date',
  [LeafType.DECIMAL]: 'Decimal',
  [LeafType.INTEGER]: 'Integer',
  [LeafType.JSON]: 'Json',
  [LeafType.TEXT]: 'Text',
  [LeafType.BENCHLING_OBJECT]: 'Benchling object',
  [LeafType.BLOB]: 'Blob',
  [LeafType.DROPDOWN_OPTION]: 'Dropdown option',
  [LeafType.CHART]: 'Chart',
  [LeafType.FILE]: 'File',
  [LeafType.SCRATCH_FILE]: 'Scratch file',
  [LeafType.DATA_FRAME]: 'Data frame',
  [LeafType.DATASET]: 'Dataset',
  [LeafType.BATCH]: 'Batch',
  [LeafType.BIOENTITY]: 'Bioentity',
  [LeafType.BASIC_FOLDER_ITEM]: 'Custom entity',
  [LeafType.MIXTURE]: 'Mixture',
  [LeafType.MOLECULE]: 'Molecule',
  [LeafType.PROTEIN]: 'Protein',
  [LeafType.SEQUENCE]: 'Sequence',
  [LeafType.ENTRY]: 'Entry',
  [LeafType.PROTOCOL]: 'Protocol',
  [LeafType.REQUEST]: 'Request',
  [LeafType.RESULT]: 'Result',
  [LeafType.RUN]: 'Run',
  [LeafType.STORABLE]: 'Storable',
  [LeafType.BOX]: 'Box',
  [LeafType.CONTAINER]: 'Container',
  [LeafType.LOCATION]: 'Location',
  [LeafType.PLATE]: 'Plate',
  [LeafType.PROCESS]: 'Workflow',
  [LeafType.PROCESS_TASK]: 'Workflow task',
  [LeafType.PROCESS_OUTPUT]: 'Workflow output',
  [LeafType.STUDY]: 'Study',
  [LeafType.TEAM]: 'Team',
  [LeafType.TEST_ORDER]: 'Test order',
  [LeafType.USER]: 'User',
  [LeafType.WORKSHEET]: 'Worksheet',
};

/**
 * Utility type to define the non-null serialized json type of each LeafType. It's useful when typing variables
 * that are directly related to a known LeafType.
 *
 * If the usage of this type gives a type error when accessing a leaf type's ts type, it means the type is not
 * complete and needs to be updated.
 *
 * These types are only correct type-wise for column types that aren't maybeText, isNull, or isList, and separate
 * types should be made to handle those cases.
 */
// Keep in sync with returns types in benchling/data_types/benchling_type_def/value_validators/leaf_type_validators.py
export interface LeafTypeToTSType {
  [LeafType.BOOLEAN]: boolean;
  [LeafType.DATETIME]: string;
  [LeafType.DATE]: string;
  [LeafType.DECIMAL]: string;
  [LeafType.INTEGER]: number;
  [LeafType.JSON]: JSONValue;
  [LeafType.TEXT]: string;

  [LeafType.BENCHLING_OBJECT]: string;
  [LeafType.BLOB]: string;
  [LeafType.DROPDOWN_OPTION]: string;
  [LeafType.BATCH]: string;
  [LeafType.CHART]: string;
  [LeafType.DATA_FRAME]: string;
  [LeafType.DATASET]: string;
  [LeafType.FILE]: string;
  [LeafType.SCRATCH_FILE]: string;
  [LeafType.BIOENTITY]: string;
  [LeafType.BASIC_FOLDER_ITEM]: string;
  [LeafType.MIXTURE]: string;
  [LeafType.MOLECULE]: string;
  [LeafType.PROTEIN]: string;
  [LeafType.SEQUENCE]: string;
  [LeafType.ENTRY]: string;
  [LeafType.PROTOCOL]: string;
  [LeafType.REQUEST]: string;
  [LeafType.RESULT]: string;
  [LeafType.RUN]: string;
  [LeafType.STORABLE]: string;
  [LeafType.BOX]: string;
  [LeafType.CONTAINER]: string;
  [LeafType.LOCATION]: string;
  [LeafType.PLATE]: string;
  [LeafType.PROCESS]: string;
  [LeafType.PROCESS_TASK]: string;
  [LeafType.PROCESS_OUTPUT]: string;
  [LeafType.STUDY]: string;
  [LeafType.TEAM]: string;
  [LeafType.TEST_ORDER]: string;
  [LeafType.USER]: string;
  [LeafType.WORKSHEET]: string;
}

export type NumericalLeafTypes = LeafType.DECIMAL | LeafType.INTEGER;
export const NUMERICAL_LEAF_TYPES: Array<LeafType> = [LeafType.DECIMAL, LeafType.INTEGER];

export function isLeafTypeNumerical(leafType: LeafType): leafType is NumericalLeafTypes {
  return NUMERICAL_LEAF_TYPES.includes(leafType);
}

export type DateLeafTypes = LeafType.DATE | LeafType.DATETIME;
export const DATE_LEAF_TYPES: Array<LeafType> = [LeafType.DATE, LeafType.DATETIME];

export function isLeafTypeDate(leafType: LeafType): leafType is DateLeafTypes {
  return DATE_LEAF_TYPES.includes(leafType);
}

export function assertLeafTypeNumerical(leafType: LeafType): asserts leafType is NumericalLeafTypes {
  assert(isLeafTypeNumerical(leafType), 'Expected column leaf type to be numerical', {leafType});
}

export function isBenchlingTypeDefAny(typeDef: BenchlingTypeDef): typeDef is AnyType {
  return typeDef instanceof AnyType;
}

export function isBenchlingTypeDefComplex(typeDef: BenchlingTypeDef): typeDef is ComplexType {
  return typeDef instanceof ComplexType;
}

export function isBenchlingTypeDefList(typeDef: BenchlingTypeDef): typeDef is List {
  return typeDef instanceof List;
}

export function isBenchlingTypeDefNullable(typeDef: BenchlingTypeDef): typeDef is Nullable {
  return typeDef instanceof Nullable;
}

export function isBenchlingTypeDefUnion(typeDef: BenchlingTypeDef): typeDef is Union {
  return typeDef instanceof Union;
}

export function isBenchlingTypeLeafType(typeDef: BenchlingTypeDef): typeDef is LeafType {
  return !isBenchlingTypeDefComplex(typeDef) && !isBenchlingTypeDefAny(typeDef);
}

// Keep in sync with the inheritance pattern in packages/benchling-type-defs/benchling_type_defs.
export const LEAF_TYPE_TO_CHILDREN: {[key in LeafType]?: Array<LeafType>} = {
  [LeafType.BIOENTITY]: [
    LeafType.BASIC_FOLDER_ITEM,
    LeafType.PROTEIN,
    LeafType.MOLECULE,
    LeafType.MIXTURE,
    LeafType.SEQUENCE,
  ],
  [LeafType.DATETIME]: [LeafType.DATE],
  [LeafType.DECIMAL]: [LeafType.INTEGER],
  [LeafType.STORABLE]: [LeafType.BOX, LeafType.CONTAINER, LeafType.LOCATION, LeafType.PLATE],
};

// Keep in sync with the children of PrimitiveTypeDef in packages/benchling-type-defs/benchling_type_defs.
export type PrimitiveLeafType =
  | LeafType.BOOLEAN
  | LeafType.DATE
  | LeafType.DATETIME
  | LeafType.DECIMAL
  | LeafType.INTEGER
  | LeafType.JSON
  | LeafType.TEXT;

export const PRIMITIVE_LEAF_TYPES: Array<PrimitiveLeafType> = [
  LeafType.BOOLEAN,
  LeafType.DATE,
  LeafType.DATETIME,
  LeafType.DECIMAL,
  LeafType.INTEGER,
  LeafType.JSON,
  LeafType.TEXT,
];

export function isLeafTypePrimitive(leafType: LeafType): leafType is PrimitiveLeafType {
  return PRIMITIVE_LEAF_TYPES.includes(leafType as PrimitiveLeafType);
}

export type ObjectLeafTypes = Exclude<LeafType, PrimitiveLeafType>;

/**
 * Determines whether or not `potentialSubTypeDef` is a "subtype" of typeDef.
 *
 * This should exactly match the inheritance patterns in packages/benchling-type-defs/benchling_type_defs.
 *
 * Doesn't support comparing against `ComplexType`s and `AnyType` like the back-end equivalent
 * `data_types.benchling_type_def.utils.is_type_def_an_instanceof` does. This is mainly because there isn't
 * currently a use case for that, but that support could be added in the future if needed.
 *
 * @param potentialSubTypeDef The typeDef to evaluate
 * @param typeDef The type def to check against
 */
// Keep in sync with packages/benchling-type-defs/benchling_type_defs/utils/type_utils.py -> is_type_def_instance_of
export function isSubType(potentialSubTypeDef: BenchlingTypeDef, typeDef: LeafType): boolean {
  if (isBenchlingTypeDefComplex(potentialSubTypeDef)) {
    if (potentialSubTypeDef instanceof Union) {
      return potentialSubTypeDef.getBaseTypes().every((def) => isSubType(def, typeDef));
    }
    // TODO(BNCH-45847): support all complex types
    throw new Error(`isSubType() does not handle ${potentialSubTypeDef.getOperatorName()}`);
  }
  if (isBenchlingTypeDefAny(potentialSubTypeDef)) {
    return false;
  }
  return (
    typeDef === potentialSubTypeDef || (LEAF_TYPE_TO_CHILDREN?.[typeDef] ?? []).includes(potentialSubTypeDef)
  );
}
